import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Toaster } from "sonner";

import "@/App.css";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import AppLayout from "@/components/layout/AppLayout";
import { ForgotPassword, Login, ResetPassword } from "@/pages/Auth";
import Dashboard from "@/pages/Dashboard";
import Workflow from "@/pages/Workflow";
import Jobs from "@/pages/Jobs";
import JobDetail from "@/pages/JobDetail";
import Candidates from "@/pages/Candidates";
import CandidateDetail from "@/pages/CandidateDetail";
import { ClientDetail, Clients } from "@/pages/Clients";
import Interviews from "@/pages/Interviews";
import Tasks from "@/pages/Tasks";
import { Communications, Documents, Offers } from "@/pages/Operations";
import Analytics from "@/pages/Analytics";
import AIIntelligence from "@/pages/AIIntelligence";
import Settings from "@/pages/Settings";
import { CandidatePortal, ClientPortal } from "@/pages/Portals";

const Splash = () => (
  <div className="grid min-h-screen place-items-center bg-slate-50">
    <div className="flex flex-col items-center gap-3">
      <span className="grid h-10 w-10 place-items-center rounded bg-blue-600 text-sm font-semibold text-white">D</span>
      <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
      <p className="text-[10px] tracking-[0.2em] text-slate-400">DIGNIFYD TALENT CONNECT</p>
    </div>
  </div>
);

function Protected({ children, roles, perm }) {
  const { user, can } = useAuth();
  const location = useLocation();
  if (user === null) return <Splash />;
  if (user === false) return <Navigate to="/login" state={{ from: location }} replace />;
  const denied = (roles && !roles.includes(user.role)) || (perm && !can(perm, "view"));
  if (denied)
    return (
      <AppLayout>
        <div className="rounded-md border border-amber-200 bg-amber-50 p-6" data-testid="access-denied">
          <p className="text-sm font-medium text-amber-800">You do not have access to this area</p>
          <p className="mt-1.5 text-xs text-amber-800/80">
            Your role ({user.role_label}) does not include this module. Contact your administrator if you need access.
          </p>
        </div>
      </AppLayout>
    );
  return <AppLayout>{children}</AppLayout>;
}

function HomeRedirect() {
  const { user } = useAuth();
  if (user === null) return <Splash />;
  if (user === false) return <Navigate to="/login" replace />;
  if (user.role === "client") return <Navigate to="/portal" replace />;
  if (user.role === "candidate") return <Navigate to="/my" replace />;
  return <Navigate to="/dashboard" replace />;
}

function DashboardRouter() {
  const { user } = useAuth();
  if (user?.role === "client") return <ClientPortal />;
  if (user?.role === "candidate") return <CandidatePortal />;
  return <Dashboard />;
}

function AuthOnly({ children }) {
  const { user } = useAuth();
  if (user === null) return <Splash />;
  if (user) return <HomeRedirect />;
  return children;
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster theme="light" position="top-right" toastOptions={{ style: { background: "#ffffff", border: "1px solid #e2e8f0", color: "#0f172a" } }} />
        <Routes>
          <Route path="/login" element={<AuthOnly><Login /></AuthOnly>} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />

          <Route path="/" element={<HomeRedirect />} />
          <Route path="/dashboard" element={<Protected><DashboardRouter /></Protected>} />
          <Route path="/workflow" element={<Protected perm="workflow"><Workflow /></Protected>} />
          <Route path="/jobs" element={<Protected perm="jobs"><Jobs /></Protected>} />
          <Route path="/jobs/:jobId" element={<Protected perm="jobs"><JobDetail /></Protected>} />
          <Route path="/candidates" element={<Protected perm="candidates"><Candidates /></Protected>} />
          <Route path="/candidates/:candidateId" element={<Protected perm="candidates"><CandidateDetail /></Protected>} />
          <Route path="/talent-pool" element={<Protected perm="talent_pool"><Candidates talentPool /></Protected>} />
          <Route path="/clients" element={<Protected perm="clients"><Clients /></Protected>} />
          <Route path="/clients/:clientId" element={<Protected perm="clients"><ClientDetail /></Protected>} />
          <Route path="/interviews" element={<Protected perm="interviews"><Interviews /></Protected>} />
          <Route path="/tasks" element={<Protected perm="tasks"><Tasks /></Protected>} />
          <Route path="/communications" element={<Protected perm="communications"><Communications /></Protected>} />
          <Route path="/offers" element={<Protected perm="offers"><Offers /></Protected>} />
          <Route path="/documents" element={<Protected perm="documents"><Documents /></Protected>} />
          <Route path="/analytics" element={<Protected perm="analytics"><Analytics /></Protected>} />
          <Route path="/ai" element={<Protected perm="ai"><AIIntelligence /></Protected>} />
          <Route path="/settings" element={<Protected><Settings /></Protected>} />
          <Route path="/portal" element={<Protected roles={["client", "super_admin", "admin"]}><ClientPortal /></Protected>} />
          <Route path="/my" element={<Protected roles={["candidate", "super_admin", "admin"]}><CandidatePortal /></Protected>} />

          <Route path="*" element={<HomeRedirect />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}
